import axios, { CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getSipQueues, getSipQueuesByNextUrl } from '../api';
import { SipQueue, SipQueueResponse } from '../domain';

const useSipQueues = ({
  search,
  queueIds,
  shouldFetch = true,
}: { search?: string; queueIds?: number[]; shouldFetch?: boolean } = {}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [list, setList] = useState<SipQueue[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const axiosCancelRef = useRef<CancelTokenSource>(axios.CancelToken.source());
  const observer = useRef<IntersectionObserver | undefined>(undefined);

  const getNextPage = useCallback(async () => {
    if (nextUrl !== null) {
      setLoading(true);
      setError(false);

      let resp: SipQueueResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getSipQueuesByNextUrl(nextUrl, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp.queues]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    }
  }, [nextUrl]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);

    let resp: SipQueueResponse | undefined;
    try {
      axiosCancelRef.current = axios.CancelToken.source();
      // resp = await getCampaigns(search, filters, accessFilterId, axiosCancelRef.current);
      resp = await getSipQueues({
        search,
        queueIds,
        cancelToken: axiosCancelRef.current,
      });
    } catch (e) {
      setError(true);
      setLoading(false);
      return;
    }

    // Returns undefined if request is canceled
    if (resp === undefined) return;

    setList(resp.queues);
    setHasMore(resp.nextPageUrl !== null);
    setNextUrl(resp.nextPageUrl);
    setLoading(false);
  }, [search, queueIds]);

  /** Ref watches for element view intersection and loads more results. Note: Should only be assigned to last element in
   * a list
   * */
  const intersectionObserverRef = useCallback(
    (node: any) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          getNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, getNextPage],
  );

  useEffect(() => {
    setList([]);
  }, [search, queueIds, shouldFetch]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(false);

      let resp: SipQueueResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getSipQueues({
          search,
          queueIds,
          cancelToken: axiosCancelRef.current,
        });
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp.queues]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    };

    if (shouldFetch) {
      load();
    }

    return () => {
      // Cancel request if it has already been executed
      axiosCancelRef.current.cancel();
    };
  }, [search, queueIds, shouldFetch]);

  return { loading, error, list, hasMore, reload, intersectionObserverRef };
};

export default useSipQueues;
